import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useMutation, useQueryClient } from 'react-query';
import { CerebroTextButton } from '../../shared/components';
import { useApi } from '../../shared/context';
import { QueryKeys } from '../../shared/hooks';
import { CurrentUser, DataRoomDiligenceRequest } from '../../shared/models';
import { CerebroApiEndpoints } from '../../shared/utils/api';
import { getResponseData } from '../../shared/utils/axios';

interface DeleteDiligenceRequestDialogProps {
	dataRoomGuid: string | undefined;
	request: DataRoomDiligenceRequest | null;
	currentUser: CurrentUser;
	close: () => void;
}

export const DeleteDiligenceRequestDialog: React.FC<DeleteDiligenceRequestDialogProps> = ({
	dataRoomGuid,
	request,
	currentUser,
	close,
}) => {
	const api = useApi();
	const snackbar = useSnackbar();
	const client = useQueryClient();

	const onRemoveConfirmation = () => {
		if (request !== null) removeRequest(request);
	};

	const { mutate: removeRequest } = useMutation<void, unknown, DataRoomDiligenceRequest>(
		requestToDelete =>
			getResponseData(
				api.delete<void>(
					CerebroApiEndpoints.DataRoomDiligenceRequests.Specific(dataRoomGuid, requestToDelete.guid)
				)
			),
		{
			onSuccess: () => {
				if (currentUser.isBankUser) {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Lender(dataRoomGuid));
				} else {
					client.invalidateQueries(QueryKeys.DataRoomDiligenceRequests.Root(dataRoomGuid));
				}

				snackbar.enqueueSnackbar('Diligence Request Deleted.', { variant: 'success' });
				close();
			},
			onError: () => {
				snackbar.enqueueSnackbar('There was a problem deleting the diligence request.', { variant: 'error' });
			},
		}
	);

	return (
		<Dialog open={request !== null} onClose={close}>
			<DialogTitle>Confirm Delete</DialogTitle>
			<DialogContent>
				<DialogContentText>
					Are you sure you want to delete this diligence request? Any documents that have been associated with
					this request will remain in the document repository.
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<CerebroTextButton onClick={close}>No</CerebroTextButton>
				<CerebroTextButton onClick={onRemoveConfirmation}>Yes</CerebroTextButton>
			</DialogActions>
		</Dialog>
	);
};
